// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/user/components/NameplateMedia.module.css';
import {
	composeGifFrame,
	drawCoveredCenter,
	frameDelayMs,
	loadNameplateGif,
	type DecodedNameplateGif,
} from '@app/features/user/components/nameplateGif';
import {getNameplateMediaKind, getNameplateMediaPath} from '@fluxer/schema/src/domains/user/NameplateSchemas';
import {getCosmeticPoster} from '@app/features/user/state/CosmeticPosterRegistry';
import {hasNameplateFx, NameplateFxCanvas} from '@app/features/cosmetics/fx/FxCanvas';
import NameplateCatalog from '@app/features/user/state/NameplateCatalog';
import {clsx} from 'clsx';
import {useEffect, useLayoutEffect, useRef, useState, type SyntheticEvent} from 'react';

interface NameplateMediaProps {
	src: string;
	active: boolean;
	className?: string;
	title?: string;
	catalogId?: string;
	/** Built-in animated renderer key; resolved from the catalog when omitted. */
	fx?: string | null;
}

export function NameplateMedia({src, active, className, title, catalogId, fx}: NameplateMediaProps) {
	const fxKey = fx ?? (catalogId ? NameplateCatalog.getById(catalogId)?.fx : NameplateCatalog.nameplates.find((entry) => entry.image === src)?.fx);
	if (hasNameplateFx(fxKey)) {
		return (
			<div className={clsx(styles.crop, className)} title={title}>
				<NameplateFxCanvas fxKey={fxKey} active={active} />
			</div>
		);
	}
	return <NameplateMediaFile src={src} active={active} className={className} title={title} catalogId={catalogId} />;
}

function NameplateMediaFile({src, active, className, title, catalogId}: NameplateMediaProps) {
	// Discord-style: show the tiny static poster until first hover; only then download the animated asset.
	const poster = getCosmeticPoster(src);
	const [wasActive, setWasActive] = useState(active);
	if (active && !wasActive) setWasActive(true);
	if (poster && !wasActive) {
		return <NameplateStaticImage src={poster} className={className} title={title} />;
	}
	const kind = getNameplateMediaKind(src);
	if (kind === 'video') {
		return <NameplateVideo src={src} active={active} className={className} title={title} />;
	}
	if (kind === 'image') {
		const path = getNameplateMediaPath(src);
		if (path.endsWith('.gif')) {
			return <NameplateGif src={src} active={active} className={className} title={title} catalogId={catalogId} />;
		}
		// Animated WebP has no pause API on <img>; drive hover like video/gif via freeze canvas.
		if (path.endsWith('.webp')) {
			return <NameplateAnimatedImage src={src} active={active} className={className} title={title} />;
		}
		return <NameplateStaticImage src={src} className={className} title={title} />;
	}
	return null;
}

function NameplateVideo({src, active, className, title}: NameplateMediaProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	useEffect(() => {
		const video = videoRef.current;
		if (!video) {
			return;
		}
		if (active) {
			void video.play().catch(() => undefined);
			return;
		}
		video.pause();
	}, [active, src]);
	return (
		<div className={clsx(styles.crop, className)}>
			<video
				ref={videoRef}
				className={styles.media}
				src={src}
				muted
				loop
				playsInline
				preload="auto"
				aria-hidden={title ? undefined : true}
				title={title}
			/>
		</div>
	);
}

function NameplateStaticImage({src, className, title}: Omit<NameplateMediaProps, 'active'>) {
	return (
		<div className={clsx(styles.crop, className)}>
			<img className={styles.media} src={src} alt="" title={title} draggable={false} />
		</div>
	);
}

/**
 * Animated WebP (and similar browser-driven image animations) only play inside <img>.
 * There is no pause API, so we show the live <img> while hovered and freeze the current
 * displayed frame onto a canvas when the pointer leaves — matching mp4 pause behavior.
 */
function NameplateAnimatedImage({src, active, className, title}: NameplateMediaProps) {
	const liveImgRef = useRef<HTMLImageElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const frozenFrameRef = useRef(false);
	// Keep the live <img> mounted for one layout pass after hover ends so we can snapshot
	// the current animated frame before unmounting (conditional render would lose it).
	const [renderLive, setRenderLive] = useState(active);
	const [playbackKey, setPlaybackKey] = useState(0);

	const paintImageToCanvas = (source: CanvasImageSource, naturalWidth: number, naturalHeight: number) => {
		const canvas = canvasRef.current;
		if (!canvas || naturalWidth <= 0 || naturalHeight <= 0) {
			return;
		}
		const width = Math.max(1, canvas.clientWidth || naturalWidth);
		const height = Math.max(1, canvas.clientHeight || naturalHeight);
		if (canvas.width !== width) {
			canvas.width = width;
		}
		if (canvas.height !== height) {
			canvas.height = height;
		}
		const context = canvas.getContext('2d');
		if (!context) {
			return;
		}
		drawCoveredCenter(context, source, naturalWidth, naturalHeight, width, height);
	};

	const onPrimeLoad = (event: SyntheticEvent<HTMLImageElement>) => {
		if (frozenFrameRef.current) {
			return;
		}
		const img = event.currentTarget;
		paintImageToCanvas(img, img.naturalWidth, img.naturalHeight);
	};

	useEffect(() => {
		frozenFrameRef.current = false;
		setRenderLive(active);
		setPlaybackKey((key) => key + 1);
	}, [src]);

	useLayoutEffect(() => {
		if (active) {
			setRenderLive(true);
			return;
		}
		if (renderLive) {
			const img = liveImgRef.current;
			if (img && img.complete && img.naturalWidth > 0) {
				paintImageToCanvas(img, img.naturalWidth, img.naturalHeight);
				frozenFrameRef.current = true;
			}
			setRenderLive(false);
			setPlaybackKey((key) => key + 1);
		}
	}, [active, renderLive, src]);

	const showLive = active || renderLive;

	return (
		<div className={clsx(styles.crop, className)}>
			{!showLive ? (
				<img
					key={`prime:${src}:${playbackKey}`}
					src={src}
					alt=""
					draggable={false}
					aria-hidden
					onLoad={onPrimeLoad}
					style={{position: 'absolute', width: 0, height: 0, opacity: 0, pointerEvents: 'none'}}
				/>
			) : null}
			<canvas
				ref={canvasRef}
				className={styles.canvas}
				aria-hidden={title ? undefined : true}
				title={title}
				style={{visibility: showLive && active ? 'hidden' : 'visible'}}
			/>
			{showLive ? (
				<img
					key={`live:${src}:${playbackKey}`}
					ref={liveImgRef}
					className={styles.media}
					src={src}
					alt=""
					title={title}
					draggable={false}
					style={{visibility: active ? 'visible' : 'hidden'}}
				/>
			) : null}
		</div>
	);
}

function NameplateGif({src, active, className, title, catalogId}: NameplateMediaProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const composeRef = useRef<HTMLCanvasElement | null>(null);
	const gifRef = useRef<DecodedNameplateGif | null>(null);
	const frameIndexRef = useRef(0);
	const previousFrameRef = useRef<ImageData | null>(null);
	const [failed, setFailed] = useState(false);
	const blitComposedFrame = () => {
		const canvas = canvasRef.current;
		const compose = composeRef.current;
		const gif = gifRef.current;
		if (!canvas || !compose || !gif) {
			return;
		}
		const width = Math.max(1, canvas.clientWidth || canvas.width);
		const height = Math.max(1, canvas.clientHeight || canvas.height);
		if (canvas.width !== width) {
			canvas.width = width;
		}
		if (canvas.height !== height) {
			canvas.height = height;
		}
		const context = canvas.getContext('2d');
		if (!context) {
			return;
		}
		drawCoveredCenter(context, compose, gif.width, gif.height, canvas.width, canvas.height);
	};
	const composeIndexedFrame = (frameIndex: number) => {
		const gif = gifRef.current;
		if (!gif) {
			return;
		}
		if (!composeRef.current) {
			composeRef.current = document.createElement('canvas');
		}
		previousFrameRef.current = composeGifFrame(gif, frameIndex, composeRef.current, previousFrameRef.current);
		blitComposedFrame();
	};
	useEffect(() => {
		let cancelled = false;
		setFailed(false);
		gifRef.current = null;
		frameIndexRef.current = 0;
		previousFrameRef.current = null;
		void loadNameplateGif(src, catalogId).then((gif) => {
			if (cancelled) {
				return;
			}
			if (!gif) {
				setFailed(true);
				return;
			}
			gifRef.current = gif;
			composeIndexedFrame(0);
			frameIndexRef.current = gif.frames.length > 1 ? 1 : 0;
		});
		return () => {
			cancelled = true;
		};
	}, [src, catalogId]);
	useEffect(() => {
		if (!active || failed) {
			return;
		}
		let timer = 0;
		const tick = () => {
			const gif = gifRef.current;
			if (!gif) {
				timer = window.setTimeout(tick, 50);
				return;
			}
			const frameIndex = frameIndexRef.current;
			composeIndexedFrame(frameIndex);
			const delay = frameDelayMs(gif.frames[frameIndex]);
			frameIndexRef.current = (frameIndex + 1) % gif.frames.length;
			if (frameIndexRef.current === 0) {
				previousFrameRef.current = null;
			}
			timer = window.setTimeout(tick, delay);
		};
		timer = window.setTimeout(tick, 0);
		return () => {
			window.clearTimeout(timer);
		};
	}, [active, failed, src, catalogId]);
	useEffect(() => {
		const canvas = canvasRef.current;
		if (!canvas || typeof ResizeObserver === 'undefined') {
			return;
		}
		const observer = new ResizeObserver(() => {
			blitComposedFrame();
		});
		observer.observe(canvas);
		return () => observer.disconnect();
	}, [src, catalogId]);
	return (
		<div className={clsx(styles.crop, className)}>
			<canvas ref={canvasRef} className={styles.canvas} aria-hidden title={title} />
		</div>
	);
}
